import {
  Breadcrumbs,
  Card,
  Inline,
  Skeleton,
  Stack,
  type TabProps,
  Tabs,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useInvoiceReport,
  useInvoiceReportEmployee,
} from '@omnipresentgroup/domains';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  INVOICE_DETAILS_PATH,
  INVOICE_EMPLOYEE_DETAILS_PATH,
  INVOICES_PATH,
} from '../../../constants';
import { InvoiceReportEmployeeBreakdown } from '../../../features/invoice-reports';
import { useManagerUser } from '../../../hooks';
import { InvoiceReportActions } from '../InvoiceReportDetails/components';
import { INVOICE_REPORTS_LIST_PAGE_TITLE } from '../InvoiceReports';
import {
  InvoiceReportEmployeeBillingSummary,
  InvoiceReportEmployeeNavigation,
  InvoiceReportEmployeeSummary,
} from './components';

const BILLING_ITEMS_TAB = 'billing-items';
const EMPLOYEE_DETAILS_TAB = 'employee-details';

const TABS: TabProps[] = [
  {
    id: BILLING_ITEMS_TAB,
    title: 'Details',
  },
  {
    id: EMPLOYEE_DETAILS_TAB,
    title: 'Employee details',
  },
];

const InvoiceReportEmployeeDetailsSkeleton = () => (
  <Stack gap="24">
    <Skeleton width={150} height={25} />
    <Skeleton height={60} />
    <Skeleton height={60} />
  </Stack>
);

const BillingNotes = ({ billingNotes }: { billingNotes?: string }) =>
  billingNotes ? (
    <Card>
      <Stack m="24" gap="8">
        <Typography as="span" size="12" color="subtle" weight="medium">
          Billing notes
        </Typography>
        <Typography as="span" size="14">
          {billingNotes}
        </Typography>
      </Stack>
    </Card>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );

export const InvoiceReportEmployeeDetails = () => {
  const { invoiceId, employeeId } = useParams<{
    invoiceId: string;
    employeeId: string;
  }>();

  const [showTabs, setShowTabs] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<TabProps>(TABS[0]);

  const navigate = useNavigate();

  const { companyId, userId } = useManagerUser();

  const { data: invoice } = useInvoiceReport({
    companyId,
    invoiceId: invoiceId || '',
    enabled: !!invoiceId,
  });

  const { data: employee, isLoading: isEmployeeLoading } =
    useInvoiceReportEmployee({
      companyId,
      invoiceId: invoiceId || '',
      employeeId: employeeId || '',
      enabled: !!invoiceId && !!employeeId,
    });

  useEffect(() => {
    setSelectedTab(TABS[0]);
    setShowTabs(true);
  }, [employeeId]);

  return (
    <Stack gap="16">
      <Breadcrumbs
        crumbs={[
          { id: 'home', title: 'Home', onClick: () => navigate('/') },
          {
            id: 'invoices',
            title: INVOICE_REPORTS_LIST_PAGE_TITLE,
            onClick: () => navigate(INVOICES_PATH),
          },
          {
            id: 'invoice',
            title: invoiceId ?? '',
            onClick: () => navigate(INVOICE_DETAILS_PATH(invoiceId)),
          },
          {
            id: 'employee',
            title: employee?.fullName ?? '',
            onClick: () =>
              navigate(INVOICE_EMPLOYEE_DETAILS_PATH(invoiceId, employeeId)),
            active: true,
          },
        ]}
      />

      {isEmployeeLoading && !employee && (
        <InvoiceReportEmployeeDetailsSkeleton />
      )}

      {invoiceId && employeeId && employee && (
        <Stack gap="24">
          <Stack gap="32">
            <Inline
              minH={40}
              align="center"
              justify={'space-between'}
              css={{ flexWrap: 'wrap' }}
            >
              <Stack gap="4">
                <Typography
                  as="h3"
                  hideParagraphSpacing
                  size="24"
                  weight="medium"
                >
                  {employee.fullName}
                </Typography>
                <Typography as="span" size="16" color="subtle">
                  {employee.role}
                </Typography>
              </Stack>
              <Inline gap="16" css={{ flexWrap: 'wrap' }}>
                {invoice && <InvoiceReportActions invoice={invoice} />}
                <InvoiceReportEmployeeNavigation
                  invoiceId={invoiceId}
                  companyId={companyId}
                  currentEmployeeId={employeeId}
                  onNavigate={() => setShowTabs(false)}
                />
              </Inline>
            </Inline>
            <InvoiceReportEmployeeBillingSummary employee={employee} />
          </Stack>

          <BillingNotes billingNotes={employee?.billingNotes} />

          {showTabs && (
            <>
              <Tabs
                onTabClicked={(tab) => setSelectedTab(tab)}
                tabsToDisplay={TABS}
              />
              {selectedTab.id === BILLING_ITEMS_TAB && (
                <InvoiceReportEmployeeBreakdown
                  companyId={companyId}
                  invoiceId={invoiceId}
                  employee={employee}
                  userId={userId}
                />
              )}
              {selectedTab.id === EMPLOYEE_DETAILS_TAB && (
                <InvoiceReportEmployeeSummary employee={employee} />
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
